import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ResidueType} from 'biojs/BioConstants';
import {formatPolymerResidueSubcount} from 'biojs/Util';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {VisualHistoryPolymerMapType} from 'graphql-client/__generated/graphqlGlobalTypes';
import FragmentVisualization from 'molbio/components/visualizations/FragmentVisualization';
import {VisualizationSequenceSummary} from 'molbio/components/visualizations/SequenceVisualizationConstants';
import * as sequtil from 'molbio/SeqUtil';
import {VisualHistoryEventSourceInfoFragment} from 'molbio/visual-history/__generated/VisualHistoryEventSourceInfoFragment';
import VisualHistoryEventMetadataField from 'molbio/visual-history/VisualHistoryEventMetadataField';
import VisualHistoryEventMetadataFieldList from 'molbio/visual-history/VisualHistoryEventMetadataFieldList';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY, GRAY_DARKER} from 'ui-library/Colors';

type VisualHistoryInputPolymerMapNode =
  VisualHistoryEventSourceInfoFragment['inputs'][number]['inputPolymerMap'];
type VisualHistoryPolymerInputNode = VisualHistoryEventSourceInfoFragment['inputs'][number];

export const VISUAL_HISTORY_POLYMER_MAP_FRAGMENT = gql`
  fragment VisualHistoryPolymerMapFragment on VisualHistoryPolymerMapNode {
    mapType
    features {
      name
      color
      type
      strand
      regions {
        start
        end
      }
      metadata {
        homologyRegionOtherColor
      }
    }
    overlays {
      type
      color
      start
      end
    }
    length
    startIndex
  }
`;

export const VISUAL_HISTORY_EVENT_SOURCE_INFO_FRAGMENT = gql`
  fragment VisualHistoryEventSourceInfoFragment on VisualHistoryEventSourceInfoNode {
    name
    inputs {
      sourceEntityMetadata {
        ...VisualHistoryEventMetadataFieldFragment
      }
      inputPolymerMap {
        mapType
        fragment {
          name
          color
          type
          strand
          regions {
            start
            end
          }
        }
        length
        startIndex
        entityType
      }
      metadataFields {
        ...VisualHistoryEventMetadataFieldFragment
      }
    }
  }
`;

/**
 * Render a visual preview of the input.
 *
 * Note: this currently only works for inputs that are polymer maps with a single fragment feature.
 *
 * @param inputVisualization Visual history polymer map node to visualize.
 * @constructor
 */
export function VisualHistoryEventSourceInputPreview({
  polymerMap,
}: {
  polymerMap: VisualHistoryInputPolymerMapNode;
}): JSX.Element {
  const {fragment} = polymerMap;
  const {start, end} = fragment.regions[0];
  const fragmentSequence: VisualizationSequenceSummary = {
    start,
    end,
    sequenceLength: polymerMap.length ?? 1,
    strand: fragment.strand as StrandOfDoubleStranded,
    circular: polymerMap.mapType === VisualHistoryPolymerMapType.CIRCULAR,
    // TODO: Handle enzymes
    fivePrimeDisplayInfo: {
      enzymeId: null,
      bases: null,
    },
    threePrimeDisplayInfo: {
      enzymeId: null,
      bases: null,
    },
  };
  const residueType = polymerMap.entityType === 'protein' ? ResidueType.AA : ResidueType.NUCLEOTIDE;
  const sizeText = polymerMap.length
    ? formatPolymerResidueSubcount(
        sequtil.length(start, end, polymerMap.length),
        polymerMap.length,
        residueType
      )
    : null;
  return (
    <div
      className={css(styles.inputPolymerMap)}
      {...getTestProps({component: 'VisualHistoryEventSourceInputPreview', element: 'root'})}
    >
      <FragmentVisualization sequence={fragmentSequence} fragmentColor={fragment.color} width={110} />
      {sizeText && (
        <p
          className={css(styles.sizeText)}
          {...getTestProps({component: 'VisualHistoryEventSourceInputPreview', element: 'sizeText'})}
        >
          {sizeText}
        </p>
      )}
    </div>
  );
}

/**
 * Render the source entity value on its own row, which may be named.
 * @param input Event source input to render.
 * @constructor
 */
export function VisualHistoryEventSourceInputRow({
  input,
}: {
  input: VisualHistoryPolymerInputNode;
}): JSX.Element {
  return (
    <div className={css(styles.inputRow)} {...getTestProps({component: 'VisualHistory', element: 'source'})}>
      <div className={css(styles.inputMetadata)}>
        <div className={css(styles.entityMetadata)}>
          <VisualHistoryEventMetadataField
            name={input.sourceEntityMetadata.name}
            value={input.sourceEntityMetadata.value}
          />
        </div>
        <VisualHistoryEventMetadataFieldList fields={input.metadataFields} />
      </div>
      <VisualHistoryEventSourceInputPreview polymerMap={input.inputPolymerMap} />
    </div>
  );
}

/**
 * Render an event source header with the section name and a count of rows.
 * @param sourceInfo Source information for the event.
 * @constructor
 */
export function VisualHistoryEventSourceHeader({
  sourceInfo,
}: {
  sourceInfo: VisualHistoryEventSourceInfoFragment;
}): JSX.Element {
  const numInputs = sourceInfo.inputs.length;
  const displayCount = numInputs > 1 ? ` (${numInputs})` : '';
  return (
    <p
      className={css(styles.header)}
      {...getTestProps({component: 'VisualHistoryEventSource', element: 'header'})}
    >{`${sourceInfo.name}${displayCount}`}</p>
  );
}

export interface VisualHistoryEventSourceProps {
  sourceInfo: VisualHistoryEventSourceInfoFragment;
}

/**
 * Render an event source, which includes a header and row for each event source input.
 * @param sourceInfo Source information for the event.
 * @constructor
 */
export default function VisualHistoryEventSource({sourceInfo}: VisualHistoryEventSourceProps): JSX.Element {
  return (
    <div {...getTestProps({component: 'VisualHistoryEventSource', element: 'root'})}>
      <VisualHistoryEventSourceHeader sourceInfo={sourceInfo} />
      {sourceInfo.inputs.map((input, i) => (
        <VisualHistoryEventSourceInputRow key={`sourceInput${i}`} input={input} />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  header: {
    ...FONT_PROXIMA_TITLE,
    color: GRAY_DARKER,
  },
  inputRow: {
    display: 'flex',
    borderTop: `1px solid ${GRAY}`,
  },
  inputMetadata: {
    minWidth: '200px',
    flex: 2,
    padding: '8px 8px 8px 0',
  },
  entityMetadata: {
    marginBottom: '12px',
  },
  inputPolymerMap: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderLeft: `1px solid ${GRAY}`,
  },
  sizeText: {
    textAlign: 'center',
  },
});
